import { useState } from 'react';
import { Activity, ExternalLink } from 'lucide-react';
import { Card } from 'app/javascript/components/ui/card';
import { Badge } from 'app/javascript/components/ui/badge';
import { Button } from 'app/javascript/components/ui/button';
import { adminService } from 'app/javascript/services/admin';
import { AdminJobStats } from 'app/javascript/types/admin';

const METRICS: { key: keyof AdminJobStats; label: string; alert?: boolean }[] = [
  { key: 'processed', label: 'Processados' },
  { key: 'failed', label: 'Falhos', alert: true },
  { key: 'enqueued', label: 'Na fila' },
  { key: 'retry_size', label: 'Retentativas', alert: true },
  { key: 'dead_size', label: 'Mortos', alert: true },
];

// Job health straight from Sidekiq/Redis. `jobs` comes null when Redis is
// unreachable — that must read as an explicit outage, never as a healthy zero.
export default function JobsHealthCard({ jobs }: { jobs: AdminJobStats | null }) {
  const [openingSidekiq, setOpeningSidekiq] = useState(false);
  const [sidekiqError, setSidekiqError] = useState(false);

  const openSidekiq = async () => {
    setOpeningSidekiq(true);
    setSidekiqError(false);

    try {
      const { url } = await adminService.createSidekiqSession();
      window.location.assign(url);
    } catch {
      setSidekiqError(true);
      setOpeningSidekiq(false);
    }
  };

  return (
    <Card className="gap-3 p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Activity className="h-4 w-4" />
          <span className="text-xs font-medium uppercase tracking-wide">Jobs (Sidekiq)</span>
        </div>
        <Button
          type="button"
          variant="link"
          size="sm"
          disabled={openingSidekiq}
          onClick={openSidekiq}
          className="h-auto gap-1 p-0 text-xs text-yellow-400"
        >
          {openingSidekiq ? 'Abrindo…' : 'Abrir /sidekiq'}
          <ExternalLink className="h-3 w-3" />
        </Button>
      </div>
      {sidekiqError && (
        <p className="text-xs text-red-400">Não foi possível autorizar o acesso ao Sidekiq.</p>
      )}
      {jobs ? (
        <dl className="grid grid-cols-2 gap-3 sm:grid-cols-5">
          {METRICS.map(({ key, label, alert }) => (
            <div key={key}>
              <dt className="text-xs text-muted-foreground">{label}</dt>
              <dd
                className={`font-bebas text-2xl ${
                  alert && jobs[key] > 0 ? 'text-red-400' : 'text-foreground'
                }`}
              >
                {jobs[key].toLocaleString('pt-BR')}
              </dd>
            </div>
          ))}
        </dl>
      ) : (
        <Badge variant="destructive" className="w-fit">
          Sidekiq indisponível
        </Badge>
      )}
    </Card>
  );
}
